// Actions a workspace can ask for: real products and open models that could
// run on its images, none of them set up here. The Actions page lists them
// under "More actions", one card per product with a Request button, grouped by
// what they do.
//
// Chosen from the market as of 5 October 2026 (docs/design/ACTION_CATALOGUE.md
// has the evidence and sources). Tasks are in order of adoption within their
// category, and so are the products within a task. Open models are named only
// where their licence allows commercial use, or where we would train them on
// our own data first: Cellpose-SAM (its weights are non-commercial) and MONAI's
// Pathology Tumor Detection (its ImageNet start is non-commercial; retrain on
// its CC0 data). Left out on purpose: products that run only on
// their maker's own scanner (Hologic Genius, Scopio, CellaVision), multiplex
// immunofluorescence (needs fluorescence support first), and ideas with no
// product or permissive model behind them yet (coeliac grading,
// frozen-section enhancement, label OCR).

import { sectionsByCategory } from './states';

/**
 * Where Request takes a person: the contact form, which is told the product's
 * id as `?action=`. Until the form exists this is null, and Request thanks the
 * person where they clicked it.
 */
export const CONTACT_FORM_URL: string | null = null;

/** Something an action does, the subheading its products sit under. */
export interface CatalogueTask {
  id: string;
  name: string;
  /** A section of the Actions page (states.ts CATEGORIES). */
  category: string;
  /** What a person gets on the image, in a sentence. */
  summary: string;
  /** The stains and tissues it is made for. */
  appliesTo: string[];
}

/** One product that does a task: a company's, or an open model. */
export interface CatalogueProduct {
  /**
   * What the contact form receives: the vendor and name as a slug, so a
   * renamed product or vendor gets a new one.
   */
  id: string;
  /** As its maker writes it. */
  name: string;
  /** The company, or for an open model its maintainer, as they write their name. */
  vendor: string;
  task: string;
  ai: boolean;
  openSource?: boolean;
  /** Where it differs from its task's. */
  appliesTo?: string[];
}

export const TASKS: CatalogueTask[] = [
  // ── Quality control ──
  { id: 'artifact-detection', name: 'Artifact detection', category: 'quality-control', summary: 'A trained model outlines folds, bubbles, out-of-focus areas, debris and pen marks, and says whether the slide should be rescanned.', appliesTo: ['H&E', 'IHC'] },

  // ── Biomarker scoring ──
  { id: 'ki67', name: 'Ki-67 index', category: 'biomarker-scoring', summary: 'The proliferation index over the entire image, its hotspot or a region you draw, with every counted cell shown.', appliesTo: ['Ki-67 IHC', 'Breast'] },
  { id: 'er-pr', name: 'ER and PR scoring', category: 'biomarker-scoring', summary: 'The percentage of positive tumor nuclei and their intensity, with the Allred score and H-score, and each cell shown.', appliesTo: ['ER IHC', 'PR IHC', 'Breast'] },
  { id: 'her2', name: 'HER2 scoring, including HER2-low', category: 'biomarker-scoring', summary: 'HER2 0, ultralow, 1+, 2+ or 3+ by the ASCO/CAP rules, from the membrane staining of each tumor cell.', appliesTo: ['HER2 IHC', 'Breast'] },
  { id: 'pd-l1', name: 'PD-L1 scoring', category: 'biomarker-scoring', summary: 'The tumor proportion score (TPS) or combined positive score (CPS), with the cells it counted shown.', appliesTo: ['PD-L1 IHC'] },
  { id: 'her2-ish', name: 'HER2 dual ISH counting', category: 'biomarker-scoring', summary: 'HER2 and CEP17 signals counted in each nucleus, with the HER2/CEP17 ratio.', appliesTo: ['HER2 ISH', 'Breast'] },
  { id: 'trop2', name: 'TROP2 scoring', category: 'biomarker-scoring', summary: 'A continuous TROP2 score from each tumor cell, to select lung cancer patients for TROP2-directed therapy.', appliesTo: ['TROP2 IHC', 'Lung'] },

  // ── Cancer detection ──
  { id: 'prostate-detection', name: 'Prostate cancer detection', category: 'cancer-detection', summary: 'Suspicious areas in prostate core biopsies outlined on a heat map, with a cancer flag for the image.', appliesTo: ['H&E', 'Prostate'] },
  { id: 'breast-detection', name: 'Breast cancer detection', category: 'cancer-detection', summary: 'Invasive carcinoma, DCIS and atypia highlighted in breast biopsies and excisions.', appliesTo: ['H&E', 'Breast'] },
  { id: 'lymph-node', name: 'Lymph node metastasis detection', category: 'cancer-detection', summary: 'Micro- and macrometastases outlined and measured in lymph node sections.', appliesTo: ['H&E', 'Lymph node'] },
  { id: 'pan-cancer', name: 'Cancer detection in any organ', category: 'cancer-detection', summary: 'Areas suspicious for cancer highlighted in tissue from any organ, to point the eye before reading.', appliesTo: ['H&E'] },
  { id: 'gastric', name: 'Gastric biopsy review', category: 'cancer-detection', summary: 'Adenocarcinoma, lymphoma, dysplasia, intestinal metaplasia and H. pylori highlighted in gastric biopsies.', appliesTo: ['H&E', 'Stomach'] },
  { id: 'colon', name: 'Colon biopsy classification', category: 'cancer-detection', summary: 'Each area of a colon biopsy labeled benign, inflamed, dysplastic or malignant.', appliesTo: ['H&E', 'Colon'] },
  { id: 'second-read', name: 'Second read', category: 'cancer-detection', summary: 'Cases signed out as benign checked again, and the ones with likely cancer flagged for another look.', appliesTo: ['H&E'] },

  // ── Grading and measurement ──
  { id: 'gleason', name: 'Gleason grading', category: 'grading', summary: 'Gleason patterns mapped, with the grade group, the percentage of patterns 4 and 5, and the tumor length.', appliesTo: ['H&E', 'Prostate'] },
  { id: 'mitosis', name: 'Mitosis counting', category: 'grading', summary: 'Every mitotic figure marked, the hotspot found, and the count per mm² or per 10 high-power fields.', appliesTo: ['H&E'] },
  { id: 'perineural', name: 'Perineural invasion detection', category: 'grading', summary: 'Each place where tumor surrounds a nerve, marked.', appliesTo: ['H&E', 'Prostate'] },
  { id: 'cribriform', name: 'Cribriform pattern detection', category: 'grading', summary: 'Cribriform areas of Gleason pattern 4 highlighted.', appliesTo: ['H&E', 'Prostate'] },

  // ── Cells and tissue ──
  { id: 'nuclei', name: 'Nuclei detection and classification', category: 'cells-and-tissue', summary: 'Every nucleus outlined and typed (tumor, lymphocyte, stromal and others), with counts and densities.', appliesTo: ['H&E', 'IHC'] },
  { id: 'cellularity', name: 'Tumor cellularity', category: 'cells-and-tissue', summary: 'The percentage of tumor cells in an area, and the best area to scrape for molecular testing.', appliesTo: ['H&E'] },
  { id: 'til', name: 'TILs and the tumor microenvironment', category: 'cells-and-tissue', summary: 'Tumor-infiltrating lymphocytes as a percentage of stroma, and the immune phenotype: inflamed, excluded or desert.', appliesTo: ['H&E'] },
  { id: 'tissue-map', name: 'Tissue map', category: 'cells-and-tissue', summary: 'Tumor, stroma, necrosis, mucosa and other tissue types mapped, with the tumor–stroma ratio.', appliesTo: ['H&E'] },

  // ── Prediction from H&E ──
  { id: 'breast-risk', name: 'Breast cancer recurrence risk', category: 'prediction', summary: 'Low or high risk of distant recurrence for early HR+/HER2− breast cancer, from the H&E alone.', appliesTo: ['H&E', 'Breast'] },
  { id: 'prostate-prognosis', name: 'Prostate cancer prognosis', category: 'prediction', summary: 'The risk of metastasis over 10 years, and whether hormone therapy is likely to help.', appliesTo: ['H&E', 'Prostate'] },
  { id: 'msi', name: 'MSI screening', category: 'prediction', summary: 'The likelihood of microsatellite instability, to decide which cases need MMR or PCR testing.', appliesTo: ['H&E', 'Colorectal'] },
  { id: 'colorectal-prognosis', name: 'Colorectal cancer prognosis', category: 'prediction', summary: 'Good or poor outcome after surgery for stage II and III colorectal cancer, to guide chemotherapy.', appliesTo: ['H&E', 'Colorectal'] },
  { id: 'lung-mutation', name: 'Lung cancer mutation prediction', category: 'prediction', summary: 'The likelihood of an EGFR mutation or ALK fusion, to decide which cases to sequence first.', appliesTo: ['H&E', 'Lung'] },
  { id: 'bladder-risk', name: 'Bladder cancer risk', category: 'prediction', summary: 'The risk that non-muscle-invasive bladder cancer recurs or fails BCG treatment.', appliesTo: ['H&E', 'Bladder'] },

  // ── Specialties ──
  { id: 'derm', name: 'Dermatopathology', category: 'specialties', summary: 'Skin biopsies sorted into melanoma, basal cell, squamous cell and benign, with urgent cases first.', appliesTo: ['H&E', 'Skin'] },
  { id: 'h-pylori', name: 'H. pylori detection', category: 'specialties', summary: 'Helicobacter pylori marked in gastric biopsies, with a positive or negative call.', appliesTo: ['H&E', 'Stomach'] },
  { id: 'cervical-cytology', name: 'Cervical cytology screening', category: 'specialties', summary: 'The most abnormal cells of a Pap test gathered for review.', appliesTo: ['Pap', 'Liquid-based'] },
  { id: 'urine-cytology', name: 'Urine cytology', category: 'specialties', summary: 'Atypical and suspicious urothelial cells ranked by the Paris System, with their nucleus-to-cytoplasm ratio.', appliesTo: ['Urine cytology'] },
  { id: 'kidney', name: 'Kidney biopsy assessment', category: 'specialties', summary: 'Glomeruli counted and classified, with interstitial fibrosis and tubular atrophy measured.', appliesTo: ['H&E', 'PAS', 'Kidney'] },
  { id: 'mash', name: 'Liver MASH scoring', category: 'specialties', summary: 'Steatosis, inflammation, ballooning and fibrosis stage scored on the MASH CRN system.', appliesTo: ['H&E', 'Trichrome', 'Liver'] },
  { id: 'uc', name: 'Ulcerative colitis activity', category: 'specialties', summary: 'Disease activity scored on the Nancy, Geboes or Robarts scales.', appliesTo: ['H&E', 'Colon'] },

  // ── Assistants and search ──
  { id: 'image-chat', name: 'Ask about this image', category: 'assistants', summary: 'Questions about the open image answered in words: descriptions, findings and a differential diagnosis.', appliesTo: ['H&E'] },
  { id: 'similar-search', name: 'Similar image search', category: 'assistants', summary: 'Images and regions that look like the one you are viewing, found across your workspace.', appliesTo: ['H&E'] },

  // ── Image tools ──
  { id: 'click-to-outline', name: 'Click to outline', category: 'image-tools', summary: 'Click a gland, nucleus or region and get its exact outline as an annotation.', appliesTo: ['Any stain'] },
  { id: 'alignment', name: 'Serial section alignment', category: 'image-tools', summary: 'Consecutive sections, such as an H&E and its IHC, aligned so annotations and results carry across.', appliesTo: ['Any stain'] },
  { id: 'stain-normalization', name: 'Stain normalization', category: 'image-tools', summary: 'A color-standardized copy of the image, so the same stain looks the same across labs, scanners and imaging systems.', appliesTo: ['H&E'] },
  { id: 'virtual-staining', name: 'Virtual staining', category: 'image-tools', summary: 'Virtual H&E from unstained tissue, or special stains and IHC-like images from the H&E, without staining another section.', appliesTo: ['H&E'] },
  { id: 'de-identification', name: 'De-identification', category: 'image-tools', summary: 'A copy of the image without its label, overview or identifying details, to share outside the institution.', appliesTo: ['Any stain'] },
];

/** "University of Göttingen PathoSAM" → "university-of-gottingen-pathosam". */
export const slug = (s: string) =>
  s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** A product of a task; AI unless said otherwise. */
function product(task: string, name: string, vendor: string, more: Partial<Pick<CatalogueProduct, 'ai' | 'openSource' | 'appliesTo'>> = {}): CatalogueProduct {
  return { id: slug(`${vendor} ${name}`), name, vendor, task, ai: true, ...more };
}
const open = (task: string, name: string, vendor: string, more: Partial<Pick<CatalogueProduct, 'ai' | 'appliesTo'>> = {}) =>
  product(task, name, vendor, { openSource: true, ...more });

// Names as each maker writes them on its own site, checked 5 October 2026
// (without ™ and ®). Paige is Tempus's since August 2025: its products keep
// the Paige name and are listed under Tempus. Ibex dropped "Galen" in 2026.
// Open models carry a readable name where their own is a code id
// (TIAToolbox's resnet18-kather100k, fcn_resnet50_unet-bcss).
export const PRODUCTS: CatalogueProduct[] = [
  product('artifact-detection', 'Aperio iQC DX Software', 'Leica Biosystems'),
  product('artifact-detection', 'AiosynQC', 'Aiosyn'),
  product('artifact-detection', 'SlideQC BF', 'Indica Labs'),
  product('artifact-detection', 'AIRAQc', 'AIRA Matrix'),
  product('artifact-detection', 'SlideRightQA', 'Barco'),

  product('ki67', 'Mindpeak Breast Ki-67 RoI', 'Mindpeak'),
  product('ki67', 'Mindpeak Breast Ki67 HS', 'Mindpeak'),
  product('ki67', 'Mindpeak Breast Ki67 4R', 'Mindpeak'),
  product('ki67', 'Ki-67 APP, Breast Cancer', 'Visiopharm'),
  product('ki67', 'Aiforia Breast Cancer Ki67', 'Aiforia'),
  product('ki67', 'uPath Ki-67 (30-9) image analysis, Breast', 'Roche'),
  product('ki67', 'Ibex Breast Ki67', 'Ibex Medical Analytics'),
  product('er-pr', 'Mindpeak ER/PR', 'Mindpeak'),
  product('er-pr', 'Mindpeak Breast ER/PR RoI', 'Mindpeak'),
  product('er-pr', 'ER APP, Breast Cancer', 'Visiopharm', { appliesTo: ['ER IHC', 'Breast'] }),
  product('er-pr', 'PR APP, Breast Cancer', 'Visiopharm', { appliesTo: ['PR IHC', 'Breast'] }),
  product('er-pr', 'Aiforia Breast Cancer ER', 'Aiforia', { appliesTo: ['ER IHC', 'Breast'] }),
  product('er-pr', 'Aiforia Breast Cancer PR', 'Aiforia', { appliesTo: ['PR IHC', 'Breast'] }),
  product('er-pr', 'uPath ER (SP1) image analysis, Breast', 'Roche', { appliesTo: ['ER IHC', 'Breast'] }),
  product('er-pr', 'uPath PR (1E2) image analysis, Breast', 'Roche', { appliesTo: ['PR IHC', 'Breast'] }),
  product('er-pr', 'Ibex Breast ER/PR', 'Ibex Medical Analytics'),
  product('her2', 'HER2 (4B5), breast cancer algorithm by navify Digital Pathology', 'Roche'),
  product('her2', 'HER2 APP, Breast cancer', 'Visiopharm'),
  product('her2', 'Mindpeak Breast HER2 RoI', 'Mindpeak'),
  product('her2', 'Mindpeak Breast HER2', 'Mindpeak'),
  product('her2', 'Aiforia Breast Cancer HER2', 'Aiforia'),
  product('her2', 'Lunit SCOPE HER2', 'Lunit'),
  product('her2', 'Ibex Breast HER2', 'Ibex Medical Analytics'),
  product('pd-l1', 'Lunit SCOPE PD-L1', 'Lunit', { appliesTo: ['PD-L1 IHC', 'Bladder', 'Breast', 'Pan-cancer'] }),
  product('pd-l1', 'Mindpeak Lung (NSCLC) PD-L1', 'Mindpeak', { appliesTo: ['PD-L1 IHC', 'Lung'] }),
  product('pd-l1', 'Mindpeak Lung (NSCLC) PD-L1 (SP263) RoI', 'Mindpeak', { appliesTo: ['PD-L1 IHC', 'Lung'] }),
  product('pd-l1', 'Mindpeak PanTumor PD-L1', 'Mindpeak', { appliesTo: ['PD-L1 IHC', 'Pan-tumor'] }),
  product('pd-l1', 'uPath PD-L1 (SP263) image analysis, NSCLC', 'Roche', { appliesTo: ['PD-L1 IHC', 'Lung'] }),
  product('pd-l1', 'PD-L1 (SP142), breast carcinoma algorithm by navify Digital Pathology', 'Roche', { appliesTo: ['PD-L1 IHC', 'Breast'] }),
  product('pd-l1', 'PD-L1 APP, NSCLC', 'Visiopharm', { appliesTo: ['PD-L1 IHC', 'Lung'] }),
  product('pd-l1', 'Aiforia Lung Cancer PD-L1', 'Aiforia', { appliesTo: ['PD-L1 IHC', 'Lung'] }),
  product('pd-l1', 'Lung PD-L1 AI', 'Indica Labs', { appliesTo: ['PD-L1 IHC', 'Lung'] }),
  product('pd-l1', 'AIM-PD-L1', 'PathAI'),
  product('her2-ish', 'uPath HER2 Dual ISH image analysis, Breast', 'Roche'),
  product('trop2', 'VENTANA TROP2 (EPR20043) RxDx Device', 'Roche'),

  product('prostate-detection', 'Paige Prostate Detect', 'Tempus'),
  product('prostate-detection', 'Ibex Prostate', 'Ibex Medical Analytics'),
  product('prostate-detection', 'DeepDx Prostate', 'Deep Bio'),
  product('prostate-detection', 'Prostate AI', 'Indica Labs'),
  product('breast-detection', 'Ibex Breast', 'Ibex Medical Analytics'),
  product('breast-detection', 'Paige Breast', 'Tempus'),
  product('breast-detection', 'Cleo Breast', 'Primaa'),
  product('lymph-node', 'Paige Breast Lymph Node', 'Tempus', { appliesTo: ['H&E', 'Lymph node', 'Breast'] }),
  product('lymph-node', 'Metastasis Detection, AI', 'Visiopharm'),
  product('lymph-node', 'Aiforia Lymph Node Metastasis', 'Aiforia'),
  product('lymph-node', 'DeepPath – LYDIA', 'DeepMed'),
  open('lymph-node', 'Pathology Tumor Detection', 'Project MONAI', { appliesTo: ['H&E', 'Lymph node', 'Breast'] }),
  product('pan-cancer', 'Paige PanCancer Detect', 'Tempus'),
  product('pan-cancer', 'Mindpeak PanTumor H&E', 'Mindpeak'),
  product('gastric', 'Ibex Gastric', 'Ibex Medical Analytics'),
  product('colon', 'QAi Colon Dx', 'Qritive'),
  product('second-read', 'Ibex Prostate Detect', 'Ibex Medical Analytics', { appliesTo: ['H&E', 'Prostate'] }),

  product('gleason', 'Paige Prostate Grade & Quantify', 'Tempus'),
  product('gleason', 'Aiforia Prostate Cancer Biopsy', 'Aiforia'),
  product('mitosis', 'Aiosyn Mitosis Breast', 'Aiosyn', { appliesTo: ['H&E', 'Breast'] }),
  product('mitosis', 'MitPro', 'Histofy'),
  product('perineural', 'Paige Prostate Perineural Invasion (PNI)', 'Tempus'),
  product('perineural', 'Aiforia Prostate Cancer PNI', 'Aiforia'),
  product('cribriform', 'Aiforia Prostate Cancer G4 Cribriform', 'Aiforia'),

  // Cellpose-SAM's published weights are non-commercial (CC-BY-NC training
  // data); its code is BSD-3, and we would train it on our own set.
  open('nuclei', 'Cellpose-SAM', 'MouseLand'),
  open('nuclei', 'InstanSeg', 'University of Edinburgh'),
  open('nuclei', 'HoVer-Net', 'TIA Centre, University of Warwick'),
  product('nuclei', 'Multiplex IHC', 'Indica Labs', { appliesTo: ['Multiplex IHC'] }),
  product('nuclei', 'Nuclei Detection, Morphology', 'Visiopharm'),
  product('cellularity', 'Lung Macrodissect AI', 'Indica Labs', { appliesTo: ['H&E', 'Lung'] }),
  product('cellularity', 'CRC Macrodissect AI', 'Indica Labs', { appliesTo: ['H&E', 'Colorectal'] }),
  product('cellularity', 'Breast Macrodissect AI', 'Indica Labs', { appliesTo: ['H&E', 'Breast'] }),
  product('cellularity', 'Prostate Macrodissect AI', 'Indica Labs', { appliesTo: ['H&E', 'Prostate'] }),
  product('cellularity', 'peakDissect', 'Mindpeak'),
  product('til', 'Lunit SCOPE IO', 'Lunit'),
  product('til', 'Atlas H&E-TME', 'Aignostics'),
  open('tissue-map', 'Kather100K tissue classifier', 'TIA Centre, University of Warwick', { appliesTo: ['H&E', 'Colorectal'] }),
  open('tissue-map', 'BCSS tissue segmentation', 'TIA Centre, University of Warwick', { appliesTo: ['H&E', 'Breast'] }),

  product('breast-risk', 'ArteraAI Breast', 'Artera'),
  product('breast-risk', 'Stratipath Breast', 'Stratipath'),
  product('breast-risk', 'RlapsRisk BC', 'Waiv'),
  product('prostate-prognosis', 'ArteraAI Prostate', 'Artera'),
  product('msi', 'MSIntuit CRC', 'Waiv'),
  product('colorectal-prognosis', 'Histotype Px Colorectal', 'DoMore Diagnostics'),
  product('lung-mutation', 'Lung Prediction Panel', 'Imagene AI'),
  product('bladder-risk', 'Vesta Bladder', 'Valar Labs'),

  product('derm', 'PathAssist Derm', 'PathAI'),
  product('derm', 'Cleo Skin', 'Primaa'),
  product('h-pylori', 'Pathol.AI Stomach H. Pylori', 'pathol.ai'),
  product('cervical-cytology', 'Techcyte SureView Cervical Cytology System', 'Techcyte'),
  product('urine-cytology', 'AIxURO', 'AIxMed'),
  product('kidney', 'NephroPath GlomeruCount', 'Aiosyn'),
  product('mash', 'AIM-MASH AI Assist', 'PathAI'),
  product('uc', 'AIM-HI UC', 'PathAI'),

  product('image-chat', 'Paige Alba', 'Tempus'),
  product('image-chat', 'PathChat DX', 'Modella AI'),
  open('similar-search', 'H-optimus-0', 'Bioptimus'),
  open('similar-search', 'Midnight-12k', 'kaiko.ai'),

  open('click-to-outline', 'SAM 2', 'Meta'),
  open('click-to-outline', 'PathoSAM', 'University of Göttingen'),
  open('click-to-outline', 'Pathology NuClick Annotation', 'Project MONAI'),
  open('alignment', 'VALIS', 'Moffitt Cancer Center', { ai: false }),
  open('stain-normalization', 'torchstain', 'EIDOSLAB, University of Turin', { ai: false }),
  product('stain-normalization', 'RetroStain', 'Pictor Labs'),
  product('virtual-staining', 'ClearStain', 'Pictor Labs', { appliesTo: ['Unstained tissue'] }),
  product('virtual-staining', 'ReStain', 'Pictor Labs'),
  open('de-identification', 'WSI Anon', 'EMPAIA', { ai: false }),
];

export interface CatalogueSection {
  category: string;
  title: string;
  /** Its tasks that have products, each with them, in order. */
  tasks: Array<CatalogueTask & { products: CatalogueProduct[] }>;
  /** How many products. */
  count: number;
}

/** The catalogue as the page shows it: our sections, their tasks, the tasks' products. */
export function catalogueSections(tasks: readonly CatalogueTask[] = TASKS, products: readonly CatalogueProduct[] = PRODUCTS): CatalogueSection[] {
  return sectionsByCategory(tasks, (t) => t.category)
    .map((s) => {
      const withProducts = s.entries
        .map((t) => ({ ...t, products: products.filter((p) => p.task === t.id) }))
        .filter((t) => t.products.length > 0);
      return { category: s.entries[0].category, title: s.title, tasks: withProducts, count: withProducts.reduce((n, t) => n + t.products.length, 0) };
    })
    .filter((s) => s.count > 0);
}

/** Where Request goes for this product, or null while there is no contact form. */
export function requestHref(p: Pick<CatalogueProduct, 'id'>, form: string | null = CONTACT_FORM_URL): string | null {
  if (!form) return null;
  const url = new URL(form, window.location.origin);
  url.searchParams.set('action', p.id);
  return url.href;
}
